// src/pages/PublicSignPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PreviewPaper from '../components/prescriptions/PreviewPaper';

const API_BASE = '/api';

// Validação de CPF (11 dígitos, com dígitos verificadores)
function onlyDigits(s) { return (s || '').replace(/\D+/g, ''); }
function isValidCPF(cpf) {
  const s = onlyDigits(cpf);
  if (s.length !== 11) return false;
  if (/^(\d)\1+$/.test(s)) return false;
  const calc = (base) => {
    let sum = 0;
    for (let i = 0; i < base; i++) sum += parseInt(s[i], 10) * (base + 1 - i);
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };
  return calc(9) === parseInt(s[9], 10) && calc(10) === parseInt(s[10], 10);
}

function dataURLFromCanvas(canvas) {
  if (!canvas) return '';
  return canvas.toDataURL('image/png');
}

export default function PublicSignPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  // modos: 'new' = /api/public/contracts/sign/:token ; 'legacy' = /api/documents/by-token/:token
  const [mode, setMode] = useState(null);

  // dados (novo formato)
  const [branding, setBranding] = useState({});
  const [docNew, setDocNew] = useState(null);

  // dados (legado)
  const [docLegacy, setDocLegacy] = useState(null);

  // UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerCpf, setSignerCpf] = useState('');
  const [accepted, setAccepted] = useState(false);
  const canvasRef = useRef(null);
  const pageRef = useRef(null);

  // ======= Carrega dados =======
  useEffect(() => {
    let alive = true;

    const fetchJSON = async (url) => {
      const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const ct = r.headers.get('content-type') || '';
      if (ct.includes('application/json')) return [r, await r.json()];
      const text = await r.text();
      return [r, { __html: text }];
    };

    (async () => {
      setLoading(true);
      setError('');
      try {
        // 1) Tentativa: NOVO endpoint (sempre /api)
        const [r1, d1] = await fetchJSON(`${API_BASE}/public/contracts/sign/${token}`);
        if (r1.ok && !d1.__html) {
          if (!alive) return;
          setMode('new');
          setBranding(d1?.branding || {});
          setDocNew(d1?.document || null);
          const publicDoc = d1?.document || {};
          const isMinor = Boolean(publicDoc.patient_is_minor || publicDoc.is_minor_or_dependent);

          setSignerName(isMinor ? (publicDoc.legal_guardian_name || '') : (publicDoc.patient_name || ''));
          setSignerCpf(isMinor ? (publicDoc.legal_guardian_cpf || '') : (publicDoc.patient_cpf || ''));
          setLoading(false);
          return;
        }
        // 2) Fallback: LEGADO
        const [r2, d2] = await fetchJSON(`${API_BASE}/documents/by-token/${token}`);
        if (!r2.ok || d2.__html) throw new Error('Link inválido ou formato inesperado.');
        if (!alive) return;
        setMode('legacy');
        setDocLegacy(d2 || null);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError(e.message || 'Não foi possível carregar o documento.');
        setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [token]);

  // ======= Canvas assinatura (compatível) =======
  function clearSig() {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
  }
  function getXY(e) {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };

    const r = c.getBoundingClientRect();
    const point = e.touches?.[0] || e.changedTouches?.[0] || e;

    const scaleX = c.width / r.width;
    const scaleY = c.height / r.height;

    return {
      x: (point.clientX - r.left) * scaleX,
      y: (point.clientY - r.top) * scaleY,
    };
  }
  function drawStart(e) {
    const c = canvasRef.current; if (!c) return;
    c.isDrawing = true;
    const ctx = c.getContext('2d');
    const { x, y } = getXY(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function drawMove(e) {
    const c = canvasRef.current; if (!c || !c.isDrawing) return;
    const ctx = c.getContext('2d');
    const { x, y } = getXY(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function drawEnd() {
    const c = canvasRef.current; if (!c) return;
    c.isDrawing = false;
  }

  // ======= DAS PARTES (novo) =======
  const partiesBlock = useMemo(() => {
    if (mode !== 'new' || !docNew) return '';
    const dentista = branding?.doctor_name || '[NOME_DENTISTA]';
    const esp = branding?.specialty ? `, ${branding.specialty}` : '';
    const cro = branding?.cro_number ? `, CRO ${branding.cro_number}` : ' [CRO]';
    const endClinica = branding?.address || '[ENDERECO_CLINICA]';
    const paciente = docNew?.patient_name || '[NOME_PACIENTE]';
    const cpf = docNew?.patient_cpf ? `CPF ${docNew.patient_cpf}` : '[CPF_PACIENTE]';
    const endPaciente = docNew?.patient_address || '[ENDERECO_PACIENTE]';
    return `DAS PARTES

CONTRATANTE: ${paciente}, ${cpf}, residente e domiciliado(a) em ${endPaciente}.
CONTRATADO: ${dentista}${esp}${cro}, com consultório localizado em ${endClinica}.`;
  }, [mode, branding, docNew]);

  // ======= Enviar assinatura =======
  async function submit() {
    try {
      if (!signerName.trim()) throw new Error('Informe seu nome para assinar.');
      if (!accepted) throw new Error('Você precisa aceitar os termos para assinar.');
      if (!isValidCPF(signerCpf)) throw new Error('CPF inválido. Verifique e tente novamente.');
      const c = canvasRef.current;
      if (!c) throw new Error('Quadro de assinatura indisponível.');
      const image_data = dataURLFromCanvas(c);

      if (!image_data || image_data.length < 5000) {
        throw new Error('Faça sua assinatura antes de enviar.');
      }

      if (mode === 'new') {
        // NOVO endpoint
        const resp = await fetch(`${API_BASE}/public/contracts/sign/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            signer_name: signerName,
            signer_cpf: signerCpf,
            accepted: true,
            signature_data_url: image_data,
          }),
        });
        const ct = resp.headers.get('content-type') || '';
        const data = ct.includes('application/json') ? await resp.json() : { __html: await resp.text() };
        if (!resp.ok || data.__html) {
          throw new Error((!data.__html && data?.detail) || 'Falha ao enviar assinatura.');
        }
        // redireciona para página de confirmação
        const code = data?.validation_code || '';
        navigate(`/public/sign/done?code=${encodeURIComponent(code)}`);
        return;
      }

      // LEGADO (mantido)
      if (!docLegacy?.id) throw new Error('Documento inválido.');
      const resp = await fetch(`${API_BASE}/documents/sign/${docLegacy.id}?role=patient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ name: signerName, image_data }),
      });
      const ct = resp.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await resp.json() : { __html: await resp.text() };
      if (!resp.ok || data.__html) {
        throw new Error((!data.__html && data?.detail) || 'Falha ao enviar assinatura.');
      }
      navigate(`/public/sign/done`);
    } catch (e) {
      alert(e.message);
    }
  }

  if (loading) return <div className="p-4">Carregando…</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4">
        {/* Preview (novo) */}
        {mode === 'new' && docNew && (
          <div className="bg-white shadow-sm rounded-xl p-4 mb-4 border">
            <div className="border rounded-md overflow-auto max-h-[75vh]">
              <PreviewPaper
                captureRef={pageRef}
                branding={branding}
                content={{
                  title: docNew.title || 'Contrato',
                  parties_block: docNew.parties_block || partiesBlock || '',
                  guidance_text: docNew.rendered_contract_text || docNew.contract_text || '',
                  rendered_contract_text: docNew.rendered_contract_text || docNew.contract_text || '',
                  contract_text: docNew.rendered_contract_text || docNew.contract_text || '',
                  issue_date: docNew.issue_date,
                  doctor_name: branding?.doctor_name,
                  patient_name: docNew.patient_name,
                  patient_cpf: docNew.patient_cpf,
                  patient_address: docNew.patient_address,
                }}
                variant="contract"
              />
            </div>
          </div>
        )}

        {/* Preview (legado) */}
        {mode === 'legacy' && docLegacy && (
          <div className="bg-white shadow-sm rounded-xl p-4 mb-4 border">
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: docLegacy.content_html }} />
          </div>
        )}

        {/* Assinatura */}
        <div className="bg-white shadow-sm rounded-xl p-4 border">
          <h2 className="font-semibold mb-2">Assinar</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">
                {docNew?.patient_is_minor || docNew?.is_minor_or_dependent ? 'Nome do responsável legal' : 'Seu nome'}
              </span>
              <input className="mt-1 w-full border rounded px-3 py-2" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm">
                {docNew?.patient_is_minor || docNew?.is_minor_or_dependent ? 'CPF do responsável legal' : 'CPF (obrigatório)'}
              </span>
              <input className="mt-1 w-full border rounded px-3 py-2" value={signerCpf} onChange={(e) => setSignerCpf(e.target.value)} placeholder="000.000.000-00" />
              {!!signerCpf && !isValidCPF(signerCpf) && <div className="text-xs text-red-600 mt-1">CPF inválido</div>}
            </label>
          </div>

          <div className="mt-3">
            <span className="text-sm">Assinatura do Paciente</span>
            <canvas
              ref={canvasRef}
              width={800}
              height={200}
              style={{ touchAction: 'none' }}
              className="border rounded w-full mt-1"
              onMouseDown={drawStart}
              onMouseMove={drawMove}
              onMouseUp={drawEnd}
              onMouseLeave={drawEnd}
              onTouchStart={(e) => { e.preventDefault(); drawStart(e); }}
              onTouchMove={(e) => { e.preventDefault(); drawMove(e); }}
              onTouchEnd={(e) => { e.preventDefault(); drawEnd(e); }}
            />
            <div className="mt-2 flex gap-2">
              <button onClick={clearSig} className="px-3 py-2 border rounded">Limpar</button>
            </div>
          </div>

          <label className="mt-3 flex items-center gap-2">
            <input type="checkbox" checked={accepted} onChange={e=>setAccepted(e.target.checked)} />
            <span className="text-sm">Li e concordo com os termos deste documento.</span>
          </label>

          <button onClick={submit} className="mt-3 px-4 py-2 rounded bg-purple-600 text-white">Assinar e Enviar</button>

          <div className="text-xs text-gray-500 mt-2">
            Ao assinar, registraremos IP, data/hora e um código de validação para auditoria.
          </div>
        </div>
      </div>
    </div>
  );
}